import { Component, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { DateFormatPipe, DynamicHeight, BytesToReadablePipe, DaysToDurationPipe } from '../../../helper';
import { Country, IBillingAddress, IBusiness, IInvoice, IInvoiceResponse, IResponseMessage, IUsers, IUsersRespone, State } from '../../../models';
import { ActivatedRoute, Router } from '@angular/router';
import { BusinessService } from '../../../services/business';
import { Constants } from '../../../common';
import { CurrencyPipe, DatePipe, UpperCasePipe } from '@angular/common';
import { MatDialog, MatDialogActions, MatDialogContent, MatDialogModule } from '@angular/material/dialog';
import { GenerateInvoiceComponent } from '../generate-invoice';
import { MatButtonModule } from '@angular/material/button';
import { HttpErrorResponse } from '@angular/common/http';
import { Auth, CommonService, SnackbarService } from '../../../services';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule, MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { environment } from '../../../../environments';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';

@Component({
  selector: 'app-business-detail',
  imports: [
    DynamicHeight,
    DateFormatPipe,
    BytesToReadablePipe,
    UpperCasePipe,
    DaysToDurationPipe,
    MatButtonModule,
    MatTableModule,
    MatIconModule,
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    MatDialogContent,
    MatDialogActions,
    MatDialogModule,
    MatMenuModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule,
    CurrencyPipe
  ],
  templateUrl: './business-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './business-detail.component.scss',
})
export class BusinessDetailComponent implements OnInit {

  business: IBusiness = {} as IBusiness;
  billingAddress: IBillingAddress = {} as IBillingAddress;

  customInvoiceDataSource: IInvoice[] = [];
  usersDataSource: IUsers[] = [];
  countries: Country[] = [];
  states: State[] = [];

  isEditBusinessDetail = false;
  businessDetailForm!: FormGroup;
  newExpiryDate: Date | null = null;
  minExpiryDate = new Date(); // today
  memberLimitOptions: number[] = Array.from({ length: 40 }, (_, i) => i + 1);   // 1 → 40
  screenLimitOptions: number[] = Array.from({ length: 300 }, (_, i) => i + 1);   // 1 → 300
  storageLimitGbOptions: number[] = Array.from({ length: 146 }, (_, i) => i + 5); // 5 → 150

  displayedInvoiceColumns = ['serial_no', 'invoice_number', 'invoice_date', 'status', 'created_at', 'action_button'];
  displayedUserColumns = ['serial_no', 'full_name', 'is_admin', 'email', 'is_email_verified', 'is_active', 'action_button'];

  billingAddressForm!: FormGroup;

  isEditBillingAddress = false;
  isWatermarkEnabled = false;
  isLauncherAvailable = false;

  isEditingBusinessName = false;
  editedBusinessName = '';
  isSavingBusinessName = false;

  // @ViewChild('deleteConfirmDialogTemplate') deleteConfirmDialogTemplate!: TemplateRef<void>;
  @ViewChild('markAsPaidConfirmDialogTemplate') markAsPaidConfirmDialogTemplate!: TemplateRef<void>;
  @ViewChild('sendCopyConfirmDialogTemplate') sendCopyConfirmDialogTemplate!: TemplateRef<void>;
  @ViewChild('cancelInvoiceConfirmDialogTemplate') cancelInvoiceConfirmDialogTemplate!: TemplateRef<void>;
  @ViewChild('downloadInvoiceConfirmDialogTemplate') downloadInvoiceConfirmDialogTemplate!: TemplateRef<void>;
  @ViewChild('impersonateUserConfirmDialogTemplate') impersonateUserConfirmDialogTemplate!: TemplateRef<void>;
  @ViewChild('sendReceiptConfirmDialogTemplate') sendReceiptConfirmDialogTemplate!: TemplateRef<void>;
  @ViewChild('changePlanDialogTemplate') changePlanDialogTemplate!: TemplateRef<any>;
  @ViewChild('deleteBusinessConfirmDialogTemplate') deleteBusinessConfirmDialogTemplate!: TemplateRef<any>;

  minScreens = 1;
  maxScreens = 300;

  deleteCountdown: number = 10;
  deleteInterval: any;

  planForm = {
    screens: 1,
    expiryDate: new Date()
  };

  planSummary = {
    screens: 0,
    users: 0,
    storageGb: 0,
    validityDays: 0,
    perScreenRate: 0,
    baseAmount: 0,
    gstAmount: 0,
    totalAmount: 0
  };

  constructor(private route: ActivatedRoute, private router: Router, private businessService: BusinessService, private auth: Auth,
    public dialog: MatDialog, private commonService: CommonService, private fb: FormBuilder, private snackbarService: SnackbarService) { }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const business_id = params.get('id');
      if (business_id) {
        this.getBusinessById(+business_id);
      }
    });
  }

  getBusinessById(business_id: number) {
    this.businessService.getBusinessById(business_id).subscribe({
      next: (business: IBusiness) => {
        console.log(business);
        this.business = business;
        if (this.business?.is_watermark_enabled !== undefined) {
          this.isWatermarkEnabled = Boolean(this.business.is_watermark_enabled);
        }
        if (this.business?.is_launcher_available !== undefined) {
          this.isLauncherAvailable = Boolean(this.business.is_launcher_available);
        }
        this.initializeBusinessDetailForm();
        this.getAllInvoiceByBusinessId();
        this.getAllUsersByBusinessId();
        this.getBillingAddressByBusinessId();
        this.getBusinessSettings(this.business?.business || business_id);
      }
    })
  }

  getBusinessSettings(business_id: number) {
    this.businessService.getBusinessSettings(business_id).subscribe({
      next: (res: any) => {
        let setting = res;
        if (Array.isArray(res)) {
          setting = res[0];
        } else if (res?.results && Array.isArray(res.results)) {
          setting = res.results[0];
        } else if (res?.data?.results && Array.isArray(res.data.results)) {
          setting = res.data.results[0];
        }
        if (setting) {
          if (setting.is_watermark_enabled !== undefined) {
            this.isWatermarkEnabled = Boolean(setting.is_watermark_enabled);
            if (this.business) {
              this.business.is_watermark_enabled = this.isWatermarkEnabled;
            }
          }
          if (setting.is_launcher_available !== undefined) {
            this.isLauncherAvailable = Boolean(setting.is_launcher_available);
            if (this.business) {
              this.business.is_launcher_available = this.isLauncherAvailable;
            }
          }
        }
      },
      error: (error: HttpErrorResponse) => {
        console.error('Failed to load business settings', error);
      }
    });
  }

  getAllInvoiceByBusinessId() {
    this.businessService.getAllCustomInvoicesByBusiness(this.business.business).subscribe({
      next: (response: IInvoiceResponse) => {
        console.log(response);
        this.customInvoiceDataSource = response?.data?.results?.length > 0 ? response.data.results : [];
      },
      error: (error: HttpErrorResponse) => {
        console.log(error);
      }
    })
  }

  getAllUsersByBusinessId() {
    this.businessService.getAllUsersByBusiness(this.business.business).subscribe({
      next: (response: IUsersRespone) => {
        console.log(response);
        this.usersDataSource = response?.data?.results?.length > 0 ? response.data.results : [];
      },
      error: (error: HttpErrorResponse) => {
        console.log(error);
      }
    })
  }

  getBillingAddressByBusinessId() {
    this.businessService.getBillingAddressByBusiness(this.business.business).subscribe({
      next: (response: IBillingAddress[]) => {
        console.log(response);
        this.billingAddress = response?.length > 0 ? response[0] : {} as IBillingAddress;
        this.initializeBillingAddress();
      },
      error: (error: HttpErrorResponse) => {
        console.log(error);
      }
    })
  }

  initializeCountryState() {
    this.commonService.getCountries().subscribe((res) => {
      this.countries = res;
      if (this.billingAddressForm.value.country) {
        this.getStatesData(this.billingAddressForm.value.country)
      }
    });

    // When country changes, load states
    this.billingAddressForm.get('country')?.valueChanges.subscribe((countryId) => {
      this.billingAddressForm.get('state')?.setValue('');
      this.getStatesData(countryId);
    });
  }

  getStatesData(countryId: string) {
    if (countryId) {
      this.commonService.getStates(countryId).subscribe((res) => {
        this.states = res;
      });
    } else {
      this.states = [];
    }
  }

  onCancel() {
    this.router.navigate([Constants.APP_ROUTES.businessList]);
  }

  onGenerateInvoice(invoice?: IInvoice) {
    if (this.billingAddress?.id) {
      const dialogRef = this.dialog.open(GenerateInvoiceComponent, {
        width: '90vw',
        maxWidth: '90vw',
        disableClose: true,
        data: {
          business: this.business,
          invoice: invoice,
          is_edit: invoice ? true : false
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.getAllInvoiceByBusinessId();
        }
      });
    }
    else {
      this.snackbarService.open(Constants.NOTIFY_MESSAGES.billingAddressRequired);
    }
  }

  editInvoice(element: IInvoice) {
    this.onGenerateInvoice(element);
  }

  sendACopy(element: IInvoice) {
    const dialogRef = this.dialog.open(this.sendCopyConfirmDialogTemplate);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.businessService.sendInvoiceCopy(element).subscribe({
          next: (response: { message: string }) => {
            this.getAllInvoiceByBusinessId();
            this.snackbarService.open(response.message);
          },
          error: (error: HttpErrorResponse) => {
            this.snackbarService.open(error?.error?.message ? error.error.message : Constants.NOTIFY_MESSAGES.error);
          }
        })
      }
    });
  }

  sendReceiptMail(element: IInvoice) {
    const dialogRef = this.dialog.open(this.sendReceiptConfirmDialogTemplate);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.businessService.sendReceiptMail(element).subscribe({
          next: (response: { message: string }) => {
            this.getAllInvoiceByBusinessId();
            this.snackbarService.open(response.message);
          },
          error: (error: HttpErrorResponse) => {
            this.snackbarService.open(error?.error?.message ? error.error.message : Constants.NOTIFY_MESSAGES.error);
          }
        })
      }
    });
  }


  cancelInvoice(element: IInvoice) {
    if (!element.is_void && !element.is_payment_done) {
      const dialogRef = this.dialog.open(this.cancelInvoiceConfirmDialogTemplate);
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.businessService.cancelInvoice(element).subscribe({
            next: (response: { message: string }) => {
              this.getAllInvoiceByBusinessId();
              this.snackbarService.open(response.message);
            },
            error: (error: HttpErrorResponse) => {
              this.snackbarService.open(error?.error?.message ? error.error.message : Constants.NOTIFY_MESSAGES.error);
            }
          })
        }
      });
    }
    else {
      let message = '';
      if (element.is_void)
        message = 'This invoice is already been cancelled and cannot be cancelled again.';
      else
        message = 'The invoice is ineligible to be cancelled.';
      this.snackbarService.open(message);
    }
  }

  markAsPaid(element: IInvoice) {
    if (!element.is_void && !element.is_payment_done) {
      const dialogRef = this.dialog.open(this.markAsPaidConfirmDialogTemplate);
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.businessService.markInvoicePaymentDone(element).subscribe({
            next: (response: { message: string }) => {
              this.getAllInvoiceByBusinessId();
              this.snackbarService.open(response.message);
            },
            error: (error: HttpErrorResponse) => {
              this.snackbarService.open(error?.error?.message ? error.error.message : Constants.NOTIFY_MESSAGES.error);
            }
          })
        }
      });
    }
    else {
      let message = '';
      if (element.is_void)
        message = 'This invoice has been cancelled and cannot be marked as paid.';
      else if (element.is_payment_done)
        message = 'This invoice is already marked as paid and cannot be marked again.';
      else
        message = 'The invoice is ineligible to be marked as paid.';
      this.snackbarService.open(message);
    }
  }

  // deleteInvoice(element: IInvoice) {
  //   const dialogRef = this.dialog.open(this.deleteConfirmDialogTemplate);
  //   dialogRef.afterClosed().subscribe(result => {
  //     if(result){
  //       this.businessService.deleteCustomInvoice(element).subscribe({
  //         next: (response: { message: string }) => {
  //           this.getAllInvoiceByBusinessId();
  //           this.snackbarService.open(response.message);
  //         },
  //         error:(error:HttpErrorResponse)=>{
  //           this.snackbarService.open(error?.error?.message ? error.error.message : Constants.NOTIFY_MESSAGES.error);
  //         }
  //       })
  //     }
  //   });
  // }

  downloadInvoice(element: IInvoice) {
    // const dialogRef = this.dialog.open(this.downloadInvoiceConfirmDialogTemplate);
    // dialogRef.afterClosed().subscribe(result => {
    //   if (result) {
    this.businessService.downloadCustomInvoice(element).subscribe({
      next: (response: Blob) => {
        // Create blob URL
        const url = window.URL.createObjectURL(response);

        // Create a hidden anchor element and set attributes for download
        const a = document.createElement('a');
        a.href = url;

        // Use invoice number or any suitable file name
        const fullInvoiceNumber = element.invoice_number;
        const invoiceNumber = fullInvoiceNumber?.split('#')[1] || 'invoice';
        a.download = `${invoiceNumber}.pdf`;

        document.body.appendChild(a);
        a.click();

        // Cleanup
        a.remove();
        window.URL.revokeObjectURL(url);
      },
      error: (error: HttpErrorResponse) => {
        console.log('Download error:', error);
      }
    });
    // }
    // });
  }

  initializeBillingAddress() {
    const billingAddress: IBillingAddress = this.billingAddress ? this.billingAddress : {} as IBillingAddress;
    billingAddress.country = isNaN(+billingAddress.country) ? billingAddress.country : +billingAddress.country;
    billingAddress.state = isNaN(+billingAddress.state) ? billingAddress.state : +billingAddress.state;

    this.billingAddressForm = this.fb.group({
      id: [billingAddress.id],
      full_name: [billingAddress.full_name, [Validators.required]],
      address_1: [billingAddress.address_1, [Validators.required]],
      address_2: [billingAddress.address_2, [Validators.required]],
      country: [billingAddress.country, [Validators.required]],
      state: [billingAddress.state, [Validators.required]],
      city: [billingAddress.city, [Validators.required]],
      zip_code: [billingAddress.zip_code, [Validators.required, Validators.pattern(Constants.ZIOCODE_PATTERN)]],
      mobile_number: [billingAddress.mobile_number, [Validators.pattern(Constants.MOBILE_PATTERN)]],
    });

    this.initializeCountryState();
  }

  initializeBusinessDetailForm() {
    const b = this.business;

    const expiryDate = b.expiry_date ? new Date(b.expiry_date) : null;
    const storageGb = b.storage_limit ? Math.round(b.storage_limit / (1024 ** 3)) : 5;

    this.businessDetailForm = this.fb.group({
      expiry_date: [expiryDate, [Validators.required]],
      members_limit: [b.members_limit, [Validators.required]],
      screen_limit: [b.screen_limit, [Validators.required]],
      storage_limit: [storageGb, [Validators.required]],   // in GB
      validity_period: [b.validity_period, [Validators.required, Validators.min(1)]],
    });
  }

  onStartEditBillingAddress() {
    this.initializeBillingAddress();
    this.isEditBillingAddress = true;
  }

  onStartEditBusinessDetail() {
    this.initializeBusinessDetailForm();
    this.isEditBusinessDetail = true;
  }

  onCancelEditBusinessDetail() {
    this.isEditBusinessDetail = false;
  }

  startEditBusinessName() {
    this.editedBusinessName = this.business?.name || '';
    this.isEditingBusinessName = true;
  }

  cancelEditBusinessName() {
    this.isEditingBusinessName = false;
    this.editedBusinessName = '';
  }

  saveBusinessName() {
    const trimmedName = this.editedBusinessName?.trim();
    if (!trimmedName) {
      this.snackbarService.open('Business name cannot be empty');
      return;
    }

    if (trimmedName === this.business?.name) {
      this.isEditingBusinessName = false;
      return;
    }

    this.isSavingBusinessName = true;
    const businessId = this.business?.business || this.business?.id;

    this.businessService.updateBusinessName(businessId, trimmedName).subscribe({
      next: () => {
        this.isSavingBusinessName = false;
        this.business.name = trimmedName;
        this.isEditingBusinessName = false;
        this.snackbarService.open('Business name updated successfully');
      },
      error: (error: HttpErrorResponse) => {
        this.isSavingBusinessName = false;
        this.snackbarService.open(
          error?.error?.message ? error.error.message : (error?.error?.detail || Constants.NOTIFY_MESSAGES.error)
        );
      }
    });
  }

  onSaveBillingAddress() {
    if (this.billingAddressForm.valid) {
      this.billingAddress = this.billingAddressForm.value;
      if (this.billingAddress.id) {
        this.businessService.updateBillingAddressByBusiness(this.billingAddress).subscribe({
          next: (response: IResponseMessage) => {
            console.log(response);
            this.snackbarService.open(response?.message ? response.message : Constants.NOTIFY_MESSAGES.billingAddressUpdated);
            this.getBillingAddressByBusinessId();
            this.isEditBillingAddress = false;
          },
          error: (error: HttpErrorResponse) => {
            this.snackbarService.open(error?.error?.message ? error.error.message : Constants.NOTIFY_MESSAGES.error);
            console.log(error);
          }
        })
      }
      else {
        this.businessService.addBillingAddressByBusiness(this.billingAddress, this.business.business, this.business.user).subscribe({
          next: (response: IBillingAddress) => {
            console.log(response);
            this.getBillingAddressByBusinessId();
            this.isEditBillingAddress = false;
          },
          error: (error: HttpErrorResponse) => {
            console.log(error);
            this.snackbarService.open(error?.error?.message ? error.error.message : Constants.NOTIFY_MESSAGES.error);
          }
        })
      }
    }
  }

  onSaveBusinessDetail() {
    if (this.businessDetailForm.valid) {
      const formValue = this.businessDetailForm.value;

      const payload = {
        ...this.business,
        ...formValue,
        expiry_date: formValue.expiry_date
          ? new Date(formValue.expiry_date).toISOString().split('T')[0]
          : null,
        storage_limit: formValue.storage_limit * 1024 * 1024 * 1024
      };

      this.businessService.updateBusinessDetails(payload).subscribe({
        next: (res: IBusiness) => {
          this.snackbarService.open('Business details updated successfully');
          this.isEditBusinessDetail = false;
          this.getBusinessById(this.business.business); // refresh
        },
        error: (error: HttpErrorResponse) => {
          this.snackbarService.open(
            error?.error?.message ? error.error.message : Constants.NOTIFY_MESSAGES.error
          );
        }
      });
    }
  }

  impersonateUser(element: IUsers) {
    if (element?.id) {
      const dialogRef = this.dialog.open(this.impersonateUserConfirmDialogTemplate);
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          const url = environment.zignFlixUrl;
          this.auth.generateToken(element.id).subscribe((response: { token: string }) => {
            const jwt = response.token;
            const new_url = `${url}impersonate_user?token=${jwt}`;
            window.open(new_url, '_blank');
          });
        }
      });
    }
  }


  onWatermarkToggle(event: any) {
    const value = typeof event === 'boolean'
      ? event
      : (event && typeof event.checked === 'boolean'
        ? event.checked
        : (event && event.target && typeof event.target.checked === 'boolean'
          ? event.target.checked
          : Boolean(this.isWatermarkEnabled)));

    this.isWatermarkEnabled = value;
    this.saveWatermarkPreference(value);
  }

  saveWatermarkPreference(enabled: boolean) {
    const businessId = this.business?.business || this.business?.id;
    const isWatermark = Boolean(enabled);
    this.isWatermarkEnabled = isWatermark;

    const payload = {
      business_id: businessId,
      is_watermark_enabled: isWatermark,
      is_launcher_available: Boolean(this.isLauncherAvailable)
    };

    console.log('Updating watermark setting payload:', payload);

    this.businessService
      .updateBusinessSettings(payload)
      .subscribe({
        next: (res: any) => {
          if (res?.is_watermark_enabled !== undefined) {
            this.isWatermarkEnabled = Boolean(res.is_watermark_enabled);
            if (this.business) {
              this.business.is_watermark_enabled = this.isWatermarkEnabled;
            }
          }
          if (res?.is_launcher_available !== undefined) {
            this.isLauncherAvailable = Boolean(res.is_launcher_available);
            if (this.business) {
              this.business.is_launcher_available = this.isLauncherAvailable;
            }
          }
          this.snackbarService.open(Constants.NOTIFY_MESSAGES.waterMarkSettingUpdated);
        },
        error: (error: HttpErrorResponse) => {
          console.error('Failed to update watermark setting', error);
          this.isWatermarkEnabled = !this.isWatermarkEnabled;
          this.snackbarService.open(error?.error?.message ? error.error.message : Constants.NOTIFY_MESSAGES.error);
        },
      });
  }

  onLauncherToggle(event: any) {
    const value = typeof event === 'boolean'
      ? event
      : (event && typeof event.checked === 'boolean'
        ? event.checked
        : (event && event.target && typeof event.target.checked === 'boolean'
          ? event.target.checked
          : Boolean(this.isLauncherAvailable)));

    this.isLauncherAvailable = value;
    this.saveLauncherPreference(value);
  }

  saveLauncherPreference(enabled: boolean) {
    const businessId = this.business?.business || this.business?.id;
    const isLauncher = Boolean(enabled);
    this.isLauncherAvailable = isLauncher;

    const payload = {
      business_id: businessId,
      is_watermark_enabled: Boolean(this.isWatermarkEnabled),
      is_launcher_available: isLauncher
    };

    console.log('Updating launcher setting payload:', payload);

    this.businessService
      .updateBusinessSettings(payload)
      .subscribe({
        next: (res: any) => {
          if (res?.is_watermark_enabled !== undefined) {
            this.isWatermarkEnabled = Boolean(res.is_watermark_enabled);
            if (this.business) {
              this.business.is_watermark_enabled = this.isWatermarkEnabled;
            }
          }
          if (res?.is_launcher_available !== undefined) {
            this.isLauncherAvailable = Boolean(res.is_launcher_available);
            if (this.business) {
              this.business.is_launcher_available = this.isLauncherAvailable;
            }
          }
          this.snackbarService.open(Constants.NOTIFY_MESSAGES.launcherSettingUpdated);
        },
        error: (error: HttpErrorResponse) => {
          console.error('Failed to update launcher setting', error);
          this.isLauncherAvailable = !this.isLauncherAvailable;
          this.snackbarService.open(error?.error?.message ? error.error.message : Constants.NOTIFY_MESSAGES.error);
        },
      });
  }


  getPlanSlotDetails(screens: number): { rate: number; storage: number; users: number } {
    if (screens < 5) return { rate: 800, storage: 5, users: 3 };
    else if (screens < 10) return { rate: 760, storage: 10, users: 5 };
    else if (screens < 20) return { rate: 720, storage: 15, users: 8 };
    else if (screens < 50) return { rate: 700, storage: 25, users: 10 };
    else if (screens < 100) return { rate: 650, storage: 50, users: 20 };
    else if (screens < 200) return { rate: 600, storage: 100, users: 30 };
    else return { rate: 520, storage: 150, users: 40 };
  }

  openUpdateSubscriptionDialog() {
    this.planForm.screens = this.business.screen_limit;
    this.planForm.expiryDate = new Date(this.business.expiry_date);

    this.recalculatePlanSummary();

    this.dialog.open(this.changePlanDialogTemplate, {
      width: '520px'
    });
  }

  onPlanChange() {
    this.recalculatePlanSummary();
  }

  recalculatePlanSummary() {
    const screens = this.planForm.screens;
    const slot = this.getPlanSlotDetails(screens);

    const baseAmount = screens * slot.rate;
    const gstAmount = +(baseAmount * 0.18).toFixed(2);   // 18% GST
    const totalAmount = +(baseAmount + gstAmount).toFixed(2);

    this.planSummary = {
      screens,
      users: slot.users,
      storageGb: slot.storage,
      validityDays: 30, // or keep your logic
      perScreenRate: slot.rate,
      baseAmount,
      gstAmount,
      totalAmount
    };
  }

  confirmUpdateSubscription() {
    if (!this.planForm.expiryDate) {
      this.snackbarService.open('Please select expiry date');
      return;
    }

    const payload = {
      ...this.business,
      screen_limit: this.planSummary.screens,
      members_limit: this.planSummary.users,
      storage_limit: this.planSummary.storageGb * 1024 * 1024 * 1024,
      validity_period: this.planSummary.validityDays,
      expiry_date: this.formatDateLocal(this.planForm.expiryDate)
    };

    this.businessService.updateBusinessDetails(payload).subscribe({
      next: () => {
        this.snackbarService.open('Subscription updated successfully');
        this.dialog.closeAll();
        this.getBusinessById(this.business.business);
      },
      error: (err: HttpErrorResponse) => {
        this.snackbarService.open(err?.error?.message || 'Failed to update subscription');
      }
    });
  }

  formatDateLocal(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }


  openDeleteBusinessDialog() {
    this.deleteCountdown = 10;

    const dialogRef = this.dialog.open(this.deleteBusinessConfirmDialogTemplate, {
      disableClose: true
    });

    this.startDeleteTimer();

    dialogRef.afterClosed().subscribe(() => {
      this.clearDeleteTimer();
    });
  }

  startDeleteTimer() {
    this.deleteInterval = setInterval(() => {
      if (this.deleteCountdown > 0) {
        this.deleteCountdown--;
      } else {
        this.clearDeleteTimer();
      }
    }, 1000);
  }

  clearDeleteTimer() {
    if (this.deleteInterval) {
      clearInterval(this.deleteInterval);
    }
  }

  confirmDeleteBusiness() {
    this.businessService.deleteBusinessById(this.business.business).subscribe({
      next: (res: any) => {
        this.snackbarService.open(res?.message || 'Business deleted successfully');
        this.dialog.closeAll();
        this.router.navigate([Constants.APP_ROUTES.businessList]);
      },
      error: (error: HttpErrorResponse) => {
        this.snackbarService.open(
          error?.error?.message || Constants.NOTIFY_MESSAGES.error
        );
      }
    });
  }
}
